import { motion, AnimatePresence } from 'framer-motion';
import { Download, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface OutputContentProps {
  activeTab: string;
}

const content = {
  notes: (
    <div className="prose prose-indigo max-w-none">
      <h1>Introduction to Machine Learning</h1>
      <p>
        Machine Learning is a subset of artificial intelligence that focuses on
        developing systems that can learn from and make decisions based on data.
      </p>
      <h2>Key Concepts</h2>
      <ul>
        <li>Supervised Learning</li>
        <li>Unsupervised Learning</li>
        <li>Reinforcement Learning</li>
      </ul>
      <p>
        In this lecture, we'll explore these fundamental concepts and their
        practical applications in modern technology.
      </p>
    </div>
  ),
  mindmap: (
    <div className="flex items-center justify-center">
      <img
        src="https://images.unsplash.com/photo-1544383835-bda2bc66a55d"
        alt="Mind Map"
        className="max-w-full rounded-lg shadow-lg"
      />
    </div>
  ),
  quiz: (
    <div className="space-y-6">
      {[1, 2, 3].map((q) => (
        <div key={q} className="rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium">
            What is the primary goal of Machine Learning?
          </h3>
          <div className="mt-4 space-y-2">
            {['To process data', 'To learn patterns', 'To store information'].map(
              (option) => (
                <label key={option} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name={`q${q}`}
                    className="text-indigo-600"
                  />
                  <span>{option}</span>
                </label>
              )
            )}
          </div>
        </div>
      ))}
    </div>
  ),
  glossary: (
    <div className="space-y-4">
      {[
        {
          term: 'Machine Learning',
          definition:
            'A field of study that gives computers the ability to learn without being explicitly programmed.',
        },
        {
          term: 'Algorithm',
          definition:
            'A step-by-step procedure or formula for solving a problem.',
        },
        {
          term: 'Dataset',
          definition:
            'A collection of data used to train and test machine learning models.',
        },
      ].map((item) => (
        <div
          key={item.term}
          className="rounded-lg border border-gray-200 p-4 transition-shadow hover:shadow-md"
        >
          <dt className="font-medium text-indigo-600">{item.term}</dt>
          <dd className="mt-1 text-gray-600">{item.definition}</dd>
        </div>
      ))}
    </div>
  ),
  flashcards: (
    <div className="flex justify-center">
      <div className="w-96 rounded-lg border border-gray-200 p-6 shadow-lg">
        <div className="text-center">
          <div className="text-lg font-medium">What is Machine Learning?</div>
          <div className="mt-4 text-gray-600">Click to reveal answer</div>
        </div>
      </div>
    </div>
  ),
};

export function OutputContent({ activeTab }: OutputContentProps) {
  return (
    <div className="rounded-lg bg-white p-6 shadow-sm">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">
          Generated Content
        </h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="gap-2">
            <Save className="h-4 w-4" />
            Save
          </Button>
          <Button size="sm" className="gap-2">
            <Download className="h-4 w-4" />
            Download
          </Button>
        </div>
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.2 }}
          className="min-h-[500px]"
        >
          {content[activeTab as keyof typeof content]}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}